import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { caErr } from "@/lib/clientAdmin";
import { useSI } from "@/lib/sourcing";
import { cn } from "@/lib/utils";

const URL = "/admin/demo-requests/alerts";
const okEmail = (e) => /^\S+@\S+\.\S+$/.test(e);

// §85 Instant email alert settings for new demo requests.
export const DemoAlerts = () => {
  const r = useSI(URL);
  const [s, setS] = useState({ enabled: true, emails: ["sales@mynextbigmove.co"] });
  const [add, setAdd] = useState("");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { if (r.data) setS({ enabled: r.data.enabled !== false, emails: r.data.emails || [] }); }, [r.data]);
  const upd = (x) => { setS(x); setDirty(true); };
  const addEmail = () => {
    const e = add.trim().toLowerCase();
    if (!okEmail(e)) return toast.error("Enter a valid email.");
    if (!s.emails.includes(e)) upd({ ...s, emails: [...s.emails, e] });
    setAdd("");
  };
  const save = async () => {
    if (s.enabled && !s.emails.length) return toast.error("Add at least one email, or turn alerts off.");
    setBusy(true);
    try { await api.put(URL, s); toast.success(s.enabled ? `Alerts go to ${s.emails.length} ${s.emails.length === 1 ? "address" : "addresses"}` : "Demo alerts turned off"); setDirty(false); }
    catch (e) { toast.error(e.response?.status === 404 ? "Alert settings aren't live on the backend yet (spec §85)." : caErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-[#8224e3]/30 bg-[#8224e3]/[0.05] p-4" data-testid="demo-alerts">
      <div className="flex flex-wrap items-center gap-3">
        <BellRing className="h-4 w-4 text-[#c79bff]" />
        <p className="flex-1 text-sm text-zinc-100">Email me the moment someone requests a demo <span className="block text-[11px] text-zinc-500">Includes their name, company, email, phone, plan and message, with a link to this inbox.</span></p>
        <button role="switch" aria-checked={s.enabled} onClick={() => upd({ ...s, enabled: !s.enabled })} className={cn("relative h-6 w-11 rounded-full transition-colors", s.enabled ? "bg-[#D4AF37]" : "bg-[#27272A]")} data-testid="demo-alerts-toggle"><span className={cn("absolute left-0 top-0.5 h-5 w-5 rounded-full bg-white transition-transform", s.enabled ? "translate-x-[22px]" : "translate-x-0.5")} /></button>
      </div>
      {s.enabled && <>
        <div className="flex flex-wrap gap-1.5" data-testid="demo-alerts-emails">{s.emails.map((e) => <span key={e} className="inline-flex items-center gap-1 rounded-full border border-[#27272A] bg-[#0f0f12] px-2.5 py-1 text-xs text-zinc-200" data-testid={`demo-alerts-email-${e}`}>{e}<button onClick={() => upd({ ...s, emails: s.emails.filter((x) => x !== e) })} className="text-zinc-500 hover:text-red-400" data-testid={`demo-alerts-remove-${e}`}><X className="h-3 w-3" /></button></span>)}
          {!s.emails.length && <span className="text-xs text-amber-300">No one gets alerts yet.</span>}</div>
        <div className="flex max-w-md gap-2"><Input value={add} onChange={(e) => setAdd(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addEmail()} placeholder="Add an email" className="h-8 text-xs" data-testid="demo-alerts-input" /><Button size="sm" variant="outline" onClick={addEmail} className="h-8" data-testid="demo-alerts-add">Add</Button></div>
      </>}
      {dirty && <Button size="sm" onClick={save} disabled={busy} className="h-8 bg-[#D4AF37] text-black hover:bg-[#F3C649]" data-testid="demo-alerts-save">{busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Save alert settings</Button>}
    </div>
  );
};
